import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Clock, TrendingUp, AlertTriangle, CreditCard, Check, X } from 'lucide-react';
import { adminGetStats, adminGetAccounts, adminGetPaymentRequests, adminConfirmPayment, adminRejectPayment, adminSendPushToGym } from './AdminPanel';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ko } from 'date-fns/locale';
import Modal from '../../components/ui/Modal';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [expiringAccounts, setExpiringAccounts] = useState([]);
  const [saasStats, setSaasStats] = useState(null);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // { id, note }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    Promise.all([adminGetStats(), adminGetAccounts(), adminGetPaymentRequests()]).then(([s, accounts, payments]) => {
      setPaymentRequests(payments || []);
      setStats(s);
      const all = accounts || [];
      const today = new Date();

      setPendingAccounts(all.filter(a => a.status === 'pending'));
      setExpiringAccounts(all.filter(a => {
        if (!a.plan_expires_at || a.status !== 'active') return false;
        const days = differenceInDays(parseISO(a.plan_expires_at), new Date());
        return days >= 0 && days <= 7;
      }));

      // SaaS 수익 현황 계산
      const active = all.filter(a => a.status === 'active');
      const paying = active.filter(a =>
        a.gym_plan === 'active' &&
        a.plan_expires_at &&
        differenceInDays(parseISO(a.plan_expires_at), today) >= 0
      );
      const trial = active.filter(a =>
        a.gym_plan === 'trial' &&
        a.plan_expires_at &&
        differenceInDays(parseISO(a.plan_expires_at), today) >= 0
      );
      const expired = active.filter(a =>
        a.plan_expires_at &&
        differenceInDays(parseISO(a.plan_expires_at), today) < 0
      );
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();
      const newThisMonth = all.filter(a =>
        a.status === 'active' && a.created_at && a.created_at >= thisMonthStart
      );
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString();
      const newLastMonth = all.filter(a =>
        a.status === 'active' && a.created_at &&
        a.created_at >= lastMonthStart && a.created_at < thisMonthStart
      );
      setSaasStats({ paying, trial, expired, newThisMonth, newLastMonth });
    }).catch(e => {
      console.error('대시보드 로드 실패:', e);
      setLoadError(true);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  async function handleConfirmPayment(id) {
    setProcessingPayment(id + '_confirm');
    const req = paymentRequests.find(r => r.id === id);
    const { error } = await adminConfirmPayment(id);
    if (!error) {
      setPaymentRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'confirmed', confirmed_at: new Date().toISOString() } : r));
      if (req?.gym_id) {
        adminSendPushToGym(
          req.gym_id,
          '납부 확인 완료',
          `${req.period_months}개월 플랜이 연장되었습니다.`,
          '/settings',
        );
      }
    } else {
      setPaymentError('납부 확인 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
    setProcessingPayment(null);
  }

  async function handleRejectConfirm() {
    if (!rejectModal) return;
    const { id, note } = rejectModal;
    setRejectModal(null);
    setProcessingPayment(id + '_reject');
    const req = paymentRequests.find(r => r.id === id);
    const { error } = await adminRejectPayment(id, note || null);
    if (!error) {
      setPaymentRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected', confirmed_at: new Date().toISOString(), admin_note: note } : r));
      if (req?.gym_id) {
        adminSendPushToGym(
          req.gym_id,
          '납부 확인 거절',
          note ? `사유: ${note}` : '납부 확인 요청이 거절되었습니다.',
          '/settings',
        );
      }
    } else {
      setPaymentError('거절 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
    setProcessingPayment(null);
  }

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (loadError) return <div className="flex items-center justify-center py-20 text-red-500 text-sm">데이터를 불러오는 데 실패했습니다. 페이지를 새로고침해주세요.</div>;

  const statCards = [
    { label: '전체 헬스장', value: stats?.totalGyms ?? 0, icon: Building2, color: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20', route: '/admin/accounts' },
    { label: '승인 대기', value: stats?.pendingAccounts ?? 0, icon: Clock, color: 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20', alert: (stats?.pendingAccounts ?? 0) > 0, route: '/admin/accounts' },
    { label: '전체 회원', value: stats?.totalMembers ?? 0, icon: Users, color: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20', route: '/admin/accounts' },
    { label: '이번 달 신규', value: stats?.newGymsThisMonth ?? 0, icon: TrendingUp, color: 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-900/20', route: '/admin/accounts' },
    { label: '무료체험', value: stats?.trialGyms ?? 0, icon: Building2, color: 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700', route: '/admin/accounts' },
    { label: '만료 임박 (7일)', value: stats?.expiringPlans ?? 0, icon: AlertTriangle, color: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20', alert: (stats?.expiringPlans ?? 0) > 0, route: '/admin/accounts' },
  ];

  return (
    <div className="space-y-6 max-w-5xl">
      {paymentError && (
        <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-300">
          <span>{paymentError}</span>
          <button onClick={() => setPaymentError('')} className="ml-2 text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
        </div>
      )}
      <h1 className="text-xl font-bold text-gray-900 dark:text-white">대시보드</h1>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map(({ label, value, icon: Icon, color, alert, route }) => (
          <div key={label} onClick={() => navigate(route)} className={`card p-5 cursor-pointer hover:shadow-md transition-shadow ${alert ? 'ring-2 ring-yellow-400 dark:ring-yellow-600' : ''}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
                <Icon className="w-5 h-5" />
              </div>
              {alert && <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">확인 필요</span>}
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* 납부 확인 요청 */}
      {paymentRequests.filter(r => r.status === 'pending').length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-green-500 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <h2 className="font-semibold text-gray-900 dark:text-white">납부 확인 요청</h2>
            <span className="ml-auto text-sm font-medium text-gray-500">
              {paymentRequests.filter(r => r.status === 'pending').length}건
            </span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {paymentRequests.filter(r => r.status === 'pending').map(req => (
              <div key={req.id} className="flex items-center justify-between px-5 py-3 gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{req.gym_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{req.owner_name} · {req.owner_email}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {req.period_months}개월 연장 요청 · 입금자: <span className="font-medium text-gray-700 dark:text-gray-300">{req.sender_name}</span>
                    {req.note && <span className="text-gray-400"> · {req.note}</span>}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {req.created_at ? format(parseISO(req.created_at), 'yyyy.MM.dd HH:mm', { locale: ko }) : ''}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleConfirmPayment(req.id)}
                    disabled={!!processingPayment}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" /> 확인
                  </button>
                  <button
                    onClick={() => setRejectModal({ id: req.id, note: '' })}
                    disabled={!!processingPayment}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> 거절
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SaaS 수익 현황 */}
      {saasStats && (
        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-500" />
            SaaS 현황
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[
              { label: '유료 이용중', value: saasStats.paying.length, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20' },
              { label: '무료체험 중', value: saasStats.trial.length, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' },
              { label: '플랜 만료', value: saasStats.expired.length, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20' },
              { label: '이번달 신규', value: saasStats.newThisMonth.length, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20' },
            ].map(({ label, value, color, bg }) => (
              <div key={label} className={`${bg} rounded-xl p-3 text-center`}>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
          {/* 신규 추이 */}
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-3">
            <span>신규 가입:</span>
            <span className="font-medium text-gray-900 dark:text-white">
              이번달 {saasStats.newThisMonth.length}건
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span>
              지난달 {saasStats.newLastMonth.length}건
            </span>
            {saasStats.newThisMonth.length > saasStats.newLastMonth.length && (
              <span className="text-green-600 dark:text-green-400 font-medium">
                ▲ {saasStats.newThisMonth.length - saasStats.newLastMonth.length}건
              </span>
            )}
            {saasStats.newThisMonth.length < saasStats.newLastMonth.length && (
              <span className="text-red-600 dark:text-red-400 font-medium">
                ▼ {saasStats.newLastMonth.length - saasStats.newThisMonth.length}건
              </span>
            )}
          </div>
          {/* 체험 전환 힌트 */}
          {saasStats.trial.length > 0 && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
              💡 무료체험 중 {saasStats.trial.length}개 헬스장 — 전환 유도 적기입니다.
            </p>
          )}
        </div>
      )}

      {/* 만료 임박 계정 */}
      {expiringAccounts.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-500 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <h2 className="font-semibold text-gray-900 dark:text-white">플랜 만료 임박</h2>
            <span className="ml-auto text-sm font-medium text-gray-500">{expiringAccounts.length}건</span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {expiringAccounts.map(acc => {
              const days = differenceInDays(parseISO(acc.plan_expires_at), new Date());
              return (
                <div key={acc.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{acc.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{acc.email}</p>
                    <p className="text-xs text-gray-400">
                      만료일: {format(parseISO(acc.plan_expires_at), 'yyyy.MM.dd', { locale: ko })}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${days <= 3 ? 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20' : 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/20'}`}>
                    D-{days}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 승인 대기 계정 */}
      {pendingAccounts.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-yellow-500 flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <h2 className="font-semibold text-gray-900 dark:text-white">승인 대기 계정</h2>
            <span className="ml-auto text-sm font-medium text-gray-500">{pendingAccounts.length}건</span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {pendingAccounts.map(acc => (
              <div key={acc.id} className="flex items-center justify-between px-5 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{acc.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{acc.email}</p>
                  <p className="text-xs text-gray-400">
                    신청일: {acc.created_at ? format(parseISO(acc.created_at), 'yyyy.MM.dd HH:mm', { locale: ko }) : ''}
                  </p>
                </div>
                <span className="text-xs text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20 px-2 py-1 rounded-full font-medium">
                  대기 중
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 납부 거절 모달 */}
      <Modal isOpen={!!rejectModal} onClose={() => setRejectModal(null)} title="납부 확인 거절">
        {rejectModal && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">거절 사유를 입력해주세요. (선택)</p>
            <textarea
              className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
              rows={3}
              placeholder="거절 사유..."
              value={rejectModal.note}
              onChange={e => setRejectModal(prev => ({ ...prev, note: e.target.value }))}
            />
            <div className="flex justify-end gap-3 pt-1">
              <button onClick={() => setRejectModal(null)} className="btn-secondary">취소</button>
              <button
                onClick={handleRejectConfirm}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                거절 확정
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
